import { create } from 'zustand';
import type {
  AcceptInviteInput,
  AuthTokens,
  CurrentUser,
  LoginInput,
  RegisterInput,
  UpdateOnboardingInput,
  UpdateProfileInput,
} from '@ddp/shared';

import { apiFetch, setAccessToken, setOrganizationId } from '../api/client.js';

type AuthStatus = 'anonymous' | 'authenticated';

interface AuthState {
  currentUser: CurrentUser | null;
  status: AuthStatus;
  /** Faux tant que l'amorçage (tentative de rafraîchissement silencieux) n'a pas eu lieu. */
  initialized: boolean;
  bootstrap: () => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  acceptInvite: (input: AcceptInviteInput) => Promise<void>;
  login: (input: LoginInput) => Promise<void>;
  updateProfile: (input: UpdateProfileInput) => Promise<void>;
  /** Choix du persona et/ou marquage d'une visite guidée comme vue (ADR-0031). */
  updateOnboarding: (input: UpdateOnboardingInput) => Promise<void>;
  logout: () => Promise<void>;
}

/**
 * État de session, en mémoire uniquement.
 *
 * Le jeton d'accès vit dans le client d'API (`api/client.ts`), hors de `localStorage` : un XSS
 * n'a rien à y voler. Conséquence assumée tant que le rafraîchissement par cookie (story 1.2)
 * n'est pas livré : un rechargement de page déconnecte. C'est le compromis sécurité/confort
 * explicite du socle (PRD NFR-2).
 */
export const useAuthStore = create<AuthState>((set) => {
  /** Pose les jetons puis charge l'identité et fixe l'organisation active. */
  const establishSession = async (tokens: AuthTokens): Promise<void> => {
    setAccessToken(tokens.accessToken);
    const me = await apiFetch<CurrentUser>('/auth/me');
    // Une seule organisation à l'inscription ; le sélecteur multi-org arrive en story 1.5.
    setOrganizationId(me.memberships[0]?.organizationId ?? null);
    set({ currentUser: me, status: 'authenticated' });
  };

  return {
    currentUser: null,
    status: 'anonymous',
    initialized: false,

    /**
     * Au démarrage de l'application, tente un rafraîchissement silencieux : le cookie HttpOnly
     * de rafraîchissement survit au rechargement (contrairement au jeton d'accès, gardé en
     * mémoire). S'il est valide, la session est restaurée sans re-saisie — c'est ce qui évite de
     * retomber sur l'écran de connexion à chaque rechargement (story 1.2).
     */
    async bootstrap() {
      try {
        // Appel direct (et non `apiFetch`) : ce dernier tenterait lui-même un rafraîchissement
        // sur 401, ce qui doublerait l'appel quand aucune session n'existe.
        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (!response.ok) {
          throw new Error('no session');
        }
        await establishSession((await response.json()) as AuthTokens);
      } catch {
        // Pas de session valide : on reste anonyme, sans bruit.
      } finally {
        set({ initialized: true });
      }
    },

    async register(input) {
      const tokens = await apiFetch<AuthTokens>('/auth/register', { method: 'POST', body: input });
      await establishSession(tokens);
    },

    /**
     * Accepte une invitation : crée le compte invité et son adhésion à l'organisation, puis ouvre la
     * session comme une inscription. Le rôle et l'organisation sont portés par le jeton, pas saisis.
     */
    async acceptInvite(input) {
      const tokens = await apiFetch<AuthTokens>('/auth/invitations/accept', {
        method: 'POST',
        body: input,
      });
      await establishSession(tokens);
    },

    async login(input) {
      const tokens = await apiFetch<AuthTokens>('/auth/login', { method: 'POST', body: input });
      await establishSession(tokens);
    },

    async updateProfile(input) {
      const me = await apiFetch<CurrentUser>('/auth/me', { method: 'PATCH', body: input });
      // L'organisation active peut avoir changé de libellé : on rafraîchit aussi son en-tête.
      setOrganizationId(me.memberships[0]?.organizationId ?? null);
      set({ currentUser: me });
    },

    async updateOnboarding(input) {
      const me = await apiFetch<CurrentUser>('/auth/me/onboarding', {
        method: 'PATCH',
        body: input,
      });
      set({ currentUser: me });
    },

    async logout() {
      // Révoque le jeton de rafraîchissement côté serveur ; l'échec réseau ne doit pas empêcher
      // la déconnexion locale.
      await apiFetch('/auth/logout', { method: 'POST' }).catch(() => {});
      setAccessToken(null);
      setOrganizationId(null);
      set({ currentUser: null, status: 'anonymous' });
    },
  };
});
